import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import './ContentArticle.css';

const FAQ_ITEMS = [
  {
    q: 'Is Letter Quest free?',
    a: 'Yes. The website and games are free to play. We may show ads on the web to support hosting and development. Mobile apps may use their own ad or monetization setup as described in store listings.',
  },
  {
    q: 'Do I need an account?',
    a: 'No. You can try many games without signing in. A free account lets you save progress, streaks, and leaderboard entries consistently across sessions and devices.',
  },
  {
    q: 'What is the difference between Daily and Practice?',
    a: 'Daily puzzles are the same for everyone on your local calendar day and usually count once per day toward streaks and leaderboards where supported. Practice modes use fresh or random setups so you can keep playing without affecting your daily result.',
  },
  {
    q: 'When does the daily puzzle reset?',
    a: 'Dailies are based on your device’s local date (midnight in your timezone). If you travel across timezones, the calendar day may change accordingly.',
  },
  {
    q: 'Are keyboard shortcuts supported?',
    a: 'Games like Word Master and Word Rungs accept typing and Enter to submit. Hidden Words uses tap-to-build paths on the grid plus Submit word / Clear (see How to play).',
  },
  {
    q: 'Can I play on my phone in the browser?',
    a: 'Yes. The site is responsive. For the best experience on small screens, use landscape where a game uses a wide keyboard or grid, or try the Android app from Get the app (Google Play). An iOS App Store release is planned.',
  },
  {
    q: 'How do leaderboards work?',
    a: 'Categories differ by game (best score, streaks, etc.). You generally need to be signed in so we can attach a display name to your entry. Visit the Leaderboard page to browse categories.',
  },
  {
    q: 'What data do you collect?',
    a: 'If you create an account, we store the account and profile information needed to run sign-in. If you play while signed in, we may store game progress, scores, streaks, and leaderboard entries so features work across sessions and devices. On the web we may also collect basic usage and device information for analytics, performance, and advertising. Details are in our Privacy Policy.',
  },
  {
    q: 'Is Letter Quest for children?',
    a: 'The games are word puzzles aimed at a general audience. Parents should supervise younger players and review the Privacy Policy and store age guidance for their region.',
  },
  {
    q: 'I saw a bug or offensive name on a leaderboard. What should I do?',
    a: 'Use the support or contact channel listed on your app store listing or site footer if available. We can take feedback on display names and technical issues from there.',
  },
  {
    q: 'Will you add more games or modes?',
    a: 'We iterate on Letter Quest over time. The web and mobile apps are meant to converge on features where it makes sense. Check release notes or the hub for new modes.',
  },
  {
    q: 'Where can I read the rules for each game?',
    a: 'The How to play hub explains every mode with rules, tips, and short FAQs. Start from How to play in the footer or site navigation.',
  },
];

export default function FaqPage() {
  const jsonLd = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQ_ITEMS.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a,
          },
        })),
      }),
    []
  );

  return (
    <main className="content-article">
      <SeoHead
        title="FAQ"
        description="Frequently asked questions about Letter Quest: accounts, daily puzzles, leaderboards, mobile apps, privacy, and how to get help."
        path="/faq"
      />
      <Helmet>
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      <h1>Frequently asked questions</h1>
      <p className="content-article__meta">Quick answers about playing Letter Quest on the web</p>

      <p className="content-article__lead">
        If you do not see your question here, check the detailed <Link to="/how-to-play">How to play</Link> guide or the{' '}
        <Link to="/about">About</Link> page. Policies: <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>.
      </p>

      <div className="content-faq__list">
        {FAQ_ITEMS.map((item) => (
          <details key={item.q} className="content-faq__item">
            <summary>{item.q}</summary>
            <div className="content-faq__body">
              <p>{item.a}</p>
            </div>
          </details>
        ))}
      </div>

      <p className="content-article__footer-links">
        <Link to="/">← Back to games</Link>
      </p>
    </main>
  );
}
